export function normalizeModuleAccess(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      if (!row || typeof row !== "object") return null;
      const module = String(row.module || row.key || "").trim();
      if (!module) return null;
      return {
        module,
        view: Boolean(row.view),
        create: Boolean(row.create),
        update: Boolean(row.update),
        delete: Boolean(row.delete),
      };
    })
    .filter(Boolean);
}

function readModuleAccessFromStorage() {
  try {
    return normalizeModuleAccess(
      JSON.parse(localStorage.getItem("moduleAccess") || "[]"),
    );
  } catch {
    return [];
  }
}

function readPermissionsFromStorage() {
  try {
    const parsed = JSON.parse(localStorage.getItem("permissions") || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Map sidebar / permission keys → moduleAccess.module name */
export function resolveModuleKey(key) {
  const k = String(key || "").trim();
  if (!k) return "";
  if (k === "supply_master" || k === "supplier_master_view" || k === "supplier_master_all") {
    return "supplier_master";
  }
  if (k === "customer_master_view" || k === "customer_master_all") {
    return "customer_master";
  }
  if (k === "tracker_update_import" || k === "tracker_update_export") {
    return "tracker_update";
  }
  if (k === "Sales Approval") return "sales_approval";
  if (
    k === "sales_orders" ||
    k === "sales-order" ||
    k === "sales-orders" ||
    k === "salesOrders" ||
    k === "Sales Orders" ||
    k === "Sales Order"
  ) {
    return "sales_order";
  }
  return k.toLowerCase().replace(/\s+/g, "_");
}

function getRows(moduleAccessOrState) {
  if (Array.isArray(moduleAccessOrState)) {
    return normalizeModuleAccess(moduleAccessOrState);
  }
  if (
    moduleAccessOrState &&
    typeof moduleAccessOrState === "object" &&
    Array.isArray(moduleAccessOrState.moduleAccess)
  ) {
    return normalizeModuleAccess(moduleAccessOrState.moduleAccess);
  }
  return readModuleAccessFromStorage();
}

function getRole(moduleAccessOrState) {
  if (
    moduleAccessOrState &&
    typeof moduleAccessOrState === "object" &&
    !Array.isArray(moduleAccessOrState) &&
    moduleAccessOrState.role
  ) {
    return moduleAccessOrState.role;
  }
  return localStorage.getItem("role") || "";
}

function getPermissions(moduleAccessOrState) {
  if (
    moduleAccessOrState &&
    typeof moduleAccessOrState === "object" &&
    !Array.isArray(moduleAccessOrState) &&
    Array.isArray(moduleAccessOrState.permissions)
  ) {
    return moduleAccessOrState.permissions;
  }
  return readPermissionsFromStorage();
}

/**
 * Check CRUD capability for a module.
 * Fallback: if moduleAccess is empty, allow when flat permissions include the module.
 *
 * @param {object|array|null} userOrAccess - Redux user or moduleAccess array
 * @param {string} module
 * @param {'view'|'create'|'update'|'delete'} action
 */
export function can(userOrAccess, module, action) {
  const role = getRole(userOrAccess);
   if (
    role === "superadmin" ||
    role === "super_admin" ||
    role === "super admin" ||
    role === "admin"
  ) {
    return true;
  }

  const moduleKey = resolveModuleKey(module);
  const actionKey = String(action || "").trim().toLowerCase();
  if (
    !moduleKey ||
    !["view", "create", "update", "delete"].includes(actionKey)
  ) {
    return false;
  }

  const rows = getRows(userOrAccess);

  // No RoleAccess configured yet → fall back to menu permissions
  if (rows.length === 0) {
    const permissions = getPermissions(userOrAccess);
    return (
      permissions.includes(module) ||
      permissions.includes(moduleKey) ||
      permissions.includes(String(module || ""))
    );
  }

  const row = rows.find(
    (m) => String(m.module).toLowerCase() === moduleKey.toLowerCase(),
  );
  if (!row) return false;
  return Boolean(row[actionKey]);
}

export function getModuleAccess(user) {
  if (user && Array.isArray(user.moduleAccess)) {
    return normalizeModuleAccess(user.moduleAccess);
  }
  return readModuleAccessFromStorage();
}

/** Extract moduleAccess from login / session API payloads */
export function extractModuleAccessFromPayload(payload) {
  if (!payload || typeof payload !== "object") return [];
  const candidates = [
    payload.moduleAccess,
    payload.user?.moduleAccess,
    payload.data?.moduleAccess,
    payload.data?.user?.moduleAccess,
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return normalizeModuleAccess(c);
  }
  return [];
}
